import { useState } from 'react';

import { api, ApiError } from '../../../api/client';
import { maskPhone, type RegistrationDraft, type RegistrationRole } from '../model';
import { useAuth, type SessionUser } from '../../../state/auth';
import { Input, WizardButton } from '../../../ui';

export interface RegistrationPasswordStepProps {
  draft: RegistrationDraft;
  role: RegistrationRole;
  onSuccess: () => void;
  buttonLabel?: string;
}

export function RegistrationPasswordStep({
  draft,
  role,
  onSuccess,
  buttonLabel,
}: RegistrationPasswordStepProps) {
  const { setAuthenticatedUser } = useAuth();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const register = async () => {
    if (password.length < 8 || !/[A-ZА-Я]/.test(password) || !/\d/.test(password)) {
      return setError('Пароль: минимум 8 символов, одна заглавная буква и одна цифра.');
    }
    if (password !== repeat) {
      return setError('Пароли не совпадают.');
    }
    setBusy(true);
    setError('');
    try {
      const resolvedAccountType =
        role === 'carrier'
          ? (draft.carrierEntityType === 'company' ? 'company' : 'person')
          : (draft.accountType === 'company' ? 'company' : 'person');

      const cleanPhone = draft.phone.trim();
      const cleanEmail = draft.email.trim();

      const user = await api<SessionUser>('/api/auth/register/', {
        method: 'POST',
        body: {
          role,
          email: cleanEmail,
          password,
          phone: cleanPhone,
          sms_code: draft.smsCode || undefined,
          account_type: resolvedAccountType,
          first_name: draft.firstName.trim(),
          last_name: draft.lastName.trim(),
          company_name: draft.companyName.trim(),
          contact_name: draft.contactName.trim(),
        },
      });
      setAuthenticatedUser(user);
      onSuccess();
    } catch (nextError) {
      setError(nextError instanceof ApiError ? nextError.message : 'Не удалось создать аккаунт');
    } finally {
      setBusy(false);
    }
  };

  const isCustomer = role === 'customer';

  return (
    <form
      className={
        isCustomer
          ? 'standalone-auth__registration-form standalone-auth__registration-form--password'
          : 'carrier-registration__form carrier-registration__form--password'
      }
      data-figma-node={isCustomer ? '2787:53083' : '2787:50195'}
      onSubmit={(e) => {
        e.preventDefault();
        void register();
      }}
    >
      <div className="standalone-auth__copy">
        <h1>Придумайте пароль</h1>
        <p>Понадобится для входа с других устройств</p>
      </div>
      <div className="standalone-auth__password-content">
        <div className="request-wizard__success-box">
          <strong className="registration-phone-confirmed"><span>✓ Телефон подтверждён</span><span>{maskPhone(draft.phone)}</span></strong>
          <span>
            {isCustomer
              ? `Email ${draft.email} подтвердите потом в личном кабинете`
              : `После создания аккаунта отправим подтверждение на ${draft.email}`}
          </span>
        </div>
        <div className="request-wizard__password-fields">
          <Input
            autoComplete="new-password"
            density="compact"
            label="Пароль"
            placeholder="Минимум 8 символов"
            type="password"
            showPassword={showPasswords}
            onToggleShowPassword={() => setShowPasswords((prev) => !prev)}
            value={password}
            onChange={(event) => {
              setError('');
              setPassword(event.target.value);
            }}
          />
          <p>Латиница, цифры, минимум одна заглавная буква</p>
          <Input
            autoComplete="new-password"
            density="compact"
            label="Повторите пароль"
            placeholder="Повторите"
            type="password"
            showPassword={showPasswords}
            onToggleShowPassword={() => setShowPasswords((prev) => !prev)}
            value={repeat}
            onChange={(event) => {
              setError('');
              setRepeat(event.target.value);
            }}
          />
        </div>
      </div>
      <WizardButton disabled={busy} type="submit">
        {busy ? 'Сохраняем…' : (buttonLabel ?? (isCustomer ? 'Создать аккаунт' : 'Сохранить'))}
      </WizardButton>
      {error ? (
        <div className="form-feedback form-feedback--error" role="alert">
          {error}
        </div>
      ) : null}
    </form>
  );
}
